import React, { useCallback, useEffect, useState, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { submitMfaChallenge } from '../api';
import ErrorBanner from './components/error-banner';
import { AUTH0_MFA_TYPES } from '../../../authentication/constants';
import { mapManagementApiTypeToAuthenticationApiChallengeType, CHALLENGE_TYPES } from '../constants';
import { getIsEmail, getIsSms, getIsVoice, getIsAuthenticator } from '../helpers';
import messages from '../messages';
import style from '../style.module.css';
import { ChallengeInput } from './components/challenge-input';

/**
 * Hash text using SHA-256
 * @param {string} text
 * @returns {Promise<string>}
 */
const hashText = async (text) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * @typedef {import('../api').MfaEnrollment} MfaEnrollment
 */
/**
 * @typedef {import('react-intl').MessageDescriptor} MessageDescriptor
 */

const EnrollmentTypeMessageMap = {
  [AUTH0_MFA_TYPES.email]: {
    titleMessage: messages.challengeEmailTitle,
    subheaderMessage: messages.challengeEmailSubheader,
  },
  [AUTH0_MFA_TYPES.sms]: {
    titleMessage: messages.challengePhoneTitle,
    subheaderMessage: messages.challengeSmsSubheader,
  },
  [AUTH0_MFA_TYPES.voice]: {
    titleMessage: messages.challengePhoneTitle,
    subheaderMessage: messages.challengeVoiceSubheader,
  },
  [AUTH0_MFA_TYPES.totp]: {
    titleMessage: messages.challengeAuthenticatorTitle,
    subheaderMessage: messages.challengeAuthenticatorSubheader,
  },
};

/**
 * @param {MfaEnrollment} enrollment
 * @returns {{ titleMessage: MessageDescriptor, subheaderMessage: MessageDescriptor }}
 */
const getMessages = (enrollment) => {
  if (getIsAuthenticator(enrollment)) {
    return EnrollmentTypeMessageMap[AUTH0_MFA_TYPES.totp];
  }

  if (getIsEmail(enrollment)) {
    return EnrollmentTypeMessageMap[AUTH0_MFA_TYPES.email];
  }

  if (getIsSms(enrollment)) {
    return EnrollmentTypeMessageMap[AUTH0_MFA_TYPES.sms];
  }

  if (getIsVoice(enrollment)) {
    return EnrollmentTypeMessageMap[AUTH0_MFA_TYPES.voice];
  }

  return EnrollmentTypeMessageMap[AUTH0_MFA_TYPES.email];
};

/**
 * @param {MfaEnrollment} enrollment
 * @returns {string}
 */
const getEnrollmentDisplayName = (enrollment) => {
  if (getIsEmail(enrollment) || getIsSms(enrollment) || getIsVoice(enrollment)) {
    return enrollment.name;
  }

  return '';
};

/**
 * @typedef {import('axios').AxiosError} AxiosError
 * @param {object} props
 * @param {MfaEnrollment} props.challengedEnrollment the enrolled mfa method used to challenge the user's identity
 * @param {string} props.mfaToken token
 * @param {(accessToken: string) => void} props.onChallengePassed callback for when user passes the step-up auth challenge
 * @param {() => void} [props.onChooseOtherMethod] callback that should redirect user back to the choose mfa enrollment view
 * @param {string} props.oobCode code provided by auth0 for the specific mfa verification session
 * @param {(enrollment: MfaEnrollment) => Promise<void>} props.onResendCode
 * @param {() => void} props.onCancel
 * @param {boolean} [props.isMfaSettings]
 * @param {boolean} [props.isUnskippable] - whether this is in unskippable mode
 * @param {string} [props.userPhone] - optional. Pass this if available for phone number validation as !fraudulent
 * @param {Array<{key: string, text: string, accepted: boolean}>} [props.consents] - optional. Consent data with translated text
 * @param {MessageDescriptor} [props.consentDisclaimer] - optional. Disclaimer text for phone consent
 * @param {(err: AxiosError) => void} [props.detectAndHandleAuthTokenExpirationError] - If being used within mfa settings then the token may expire. We should handle that appropriately to avoid confusing users.
 * @returns {React.JSX.Element}
 */
export const ChallengePhaseModalContent = ({
  challengedEnrollment,
  mfaToken,
  onChallengePassed,
  onChooseOtherMethod,
  oobCode,
  onCancel,
  onResendCode,
  isMfaSettings = false,
  isUnskippable = false,
  userPhone,
  consents,
  consentDisclaimer,
  detectAndHandleAuthTokenExpirationError = noop,
}) => {
  const intl = useIntl();
  const [error, setError] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);

  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [third, setThird] = useState('');
  const [fourth, setFourth] = useState('');
  const [fifth, setFifth] = useState('');
  const [sixth, setSixth] = useState('');

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const firstRef = useRef();
  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const secondRef = useRef();
  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const thirdRef = useRef();
  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const fourthRef = useRef();
  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const fifthRef = useRef();
  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const sixthRef = useRef();

  const onSubmit = useCallback(
    /**
     * @param {React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement> | ClipboardEvent} e
     * @param {string} [mfaCode]
     */
    async (e, mfaCode) => {
      e.preventDefault();
      const code = mfaCode || `${first}${second}${third}${fourth}${fifth}${sixth}`;
      const isValid = code.length === 6 && !!code.match(/\d{6}/g);
      if (!isValid) {
        setError({ message: intl.formatMessage(messages.invalidCodeError), code: '0001' });
        return;
      }
      try {
        setError(null);
        setIsDisabled(true);

        const challengeType = mapManagementApiTypeToAuthenticationApiChallengeType[challengedEnrollment.type];
        const codeKey = challengeType === CHALLENGE_TYPES.OOB ? 'bindingCode' : 'otpCode';

        // Process consents into hashed format for security
        let consentPayload;
        if (consents && consents.length > 0) {
          try {
            const phoneConsent = consentDisclaimer ? ` ${intl.formatMessage(consentDisclaimer)}` : '';

            const hashedConsents = await Promise.all(
              consents.map(async (consent) => ({
                text_hash: await hashText(`${consent.text}${phoneConsent}`),
                accepted: consent.accepted,
              })),
            );
            consentPayload = hashedConsents;
          } catch {
            // If we can't hash, we can't assume consent is valid
            consentPayload = undefined;
          }
        }

        const { accessToken } = await submitMfaChallenge({
          [codeKey]: code,
          challengeType,
          mfaToken,
          oobCode,
          isMfaSettings,
          userPhone,
          consents: consentPayload,
        });
        onChallengePassed(accessToken);
      } catch (err) {
        detectAndHandleAuthTokenExpirationError(err);
        setError(err);
      } finally {
        setIsDisabled(false);
      }
    },
    [
      challengedEnrollment.type,
      fifth,
      first,
      fourth,
      intl,
      mfaToken,
      onChallengePassed,
      oobCode,
      second,
      sixth,
      third,
      isMfaSettings,
      userPhone,
      consents,
      consentDisclaimer,
      detectAndHandleAuthTokenExpirationError,
    ],
  );

  const onPaste = useCallback(
    /**
     * @param {React.MouseEvent<HTMLButtonElement> | ClipboardEvent} event
     */
    async (event) => {
      event.preventDefault();
      let text;
      try {
        text = await navigator.clipboard.readText();
      } catch {
        const errMsg = intl.formatMessage(messages.pasteError);
        setError({ message: errMsg, code: '0002' });
        return;
      }
      const noSpacesData = text.replace(/\s+/g, '');
      const match = noSpacesData.match(/^.*(\d{6}).*$/);
      if (!match || match.length < 1) {
        const errMsg = intl.formatMessage(messages.invalidCodeError);
        setError({ message: errMsg, code: '0002' });
        return;
      }

      match[1].split('').forEach((digit, index) => {
        switch (index) {
          case 0:
            setFirst(digit);
            break;
          case 1:
            setSecond(digit);
            break;
          case 2:
            setThird(digit);
            break;
          case 3:
            setFourth(digit);
            break;
          case 4:
            setFifth(digit);
            break;
          case 5:
            setSixth(digit);
            break;
          default:
            break;
        }
      });

      sixthRef.current?.focus();
      onSubmit(event, match[1]);
    },
    [intl, onSubmit],
  );

  useEffect(() => {
    firstRef?.current?.focus();
  }, []);

  useEffect(() => {
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('paste', onPaste);
    };
  }, [onPaste]);

  const onResend = async () => {
    try {
      setIsDisabled(true);
      await onResendCode(challengedEnrollment);
    } catch {
      setError({ message: intl.formatMessage(messages.resendCodeError), code: '0004' });
    } finally {
      setIsDisabled(false);
    }
  };

  const enrollmentName = getEnrollmentDisplayName(challengedEnrollment);
  const shouldShowResendCode = !getIsAuthenticator(challengedEnrollment);

  const { titleMessage, subheaderMessage } = getMessages(challengedEnrollment);

  return (
    <form onSubmit={onSubmit}>
      <ModalBody className={style.body}>
        <FlexColumn className={style['flex-center']} gap={Gap.Space16}>
          <Text textType={TextType.H3} value={titleMessage} />
          <Text textType={TextType.Description} value={subheaderMessage} />
          {enrollmentName ? (
            <FlexRow className={style['challenged-enrollment']}>
              <Text textType={TextType.Description} value={enrollmentName} />
            </FlexRow>
          ) : null}
          {error ? <ErrorBanner code={error.code} message={error.message} /> : null}
          <FlexColumn className={style['code-section']} gap={Gap.Space16}>
            <FlexRow className={style['flex-center']} gap={Gap.Space8}>
              <ChallengeInput ref={firstRef} isDisabled={isDisabled} nextSiblingRef={secondRef} setter={setFirst} value={first} />
              <ChallengeInput
                ref={secondRef}
                isDisabled={isDisabled}
                nextSiblingRef={thirdRef}
                prevSiblingRef={firstRef}
                setter={setSecond}
                value={second}
              />
              <ChallengeInput
                ref={thirdRef}
                isDisabled={isDisabled}
                nextSiblingRef={fourthRef}
                prevSiblingRef={secondRef}
                setter={setThird}
                value={third}
              />
              <ChallengeInput
                ref={fourthRef}
                isDisabled={isDisabled}
                nextSiblingRef={fifthRef}
                prevSiblingRef={thirdRef}
                setter={setFourth}
                value={fourth}
              />
              <ChallengeInput
                ref={fifthRef}
                isDisabled={isDisabled}
                nextSiblingRef={sixthRef}
                prevSiblingRef={fourthRef}
                setter={setFifth}
                value={fifth}
              />
              <ChallengeInput ref={sixthRef} isDisabled={isDisabled} prevSiblingRef={fifthRef} setter={setSixth} value={sixth} />
            </FlexRow>
            {shouldShowResendCode ? (
              <FlexColumn className={style['resend-wrapper']}>
                <p className={style['resend-content']}>
                  <FormattedMessage {...messages.expiryNotice} />
                </p>
                <p className={style['resend-content']}>
                  <FormattedMessage
                    {...messages.resendCode}
                    values={{
                      a: (chunks) => {
                        return (
                          <Button onClick={onResend} theme={THEME_CONSTANTS.link}>
                            {chunks}
                          </Button>
                        );
                      },
                    }}
                  />
                </p>
              </FlexColumn>
            ) : null}
            {onChooseOtherMethod ? (
              <Button disabled={isDisabled} onClick={onChooseOtherMethod} theme={THEME_CONSTANTS.link}>
                <FormattedMessage {...messages.selectDifferentMethodButton} />
              </Button>
            ) : null}
          </FlexColumn>
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexRow className={style['footer-buttons']} gap={Gap.Space16}>
          {!isUnskippable && (
            <Button disabled={isDisabled} onClick={onCancel} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.cancelButton} />
            </Button>
          )}
          <Button disabled={isDisabled} onClick={onSubmit} theme={THEME_CONSTANTS.primary} type='submit'>
            <FormattedMessage {...messages.submitButton} />
          </Button>
        </FlexRow>
      </ModalFooter>
    </form>
  );
};
